import { A11yModule } from '@angular/cdk/a11y';
import { Dir } from '@angular/cdk/bidi';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressHttp } from 'ngx-progressbar/http';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SettingsService } from '@core';

import { TwoFactorAuthMonitorComponent } from '../../routes/components/two-factor-auth/two-factor-auth-monitor/two-factor-auth-monitor.component';
import { SurveyIconComponent } from '../../shared/components/survey-icon/survey-icon.component';
import { ApiAvailabilityService } from '@core/services/api-availability.service';
import { MultiSummaryComponent } from '@shared/components/multi-summary/multi-summary.component';
import { ExitSurveyService } from '@shared/services/exit-survey.service';
import { LoginPopupService } from '@shared/services/login-popup.service';
import { LayoutService } from 'app/features/layout/services/layout.service';

import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SidebarNoticeComponent } from '../sidebar-notice/sidebar-notice.component';
import { TopmenuComponent } from '../topmenu/topmenu.component';

const MOBILE_MEDIAQUERY = 'screen and (max-width: 599px)';
const TABLET_MEDIAQUERY = 'screen and (min-width: 600px) and (max-width: 959px)';
const MONITOR_MEDIAQUERY = 'screen and (min-width: 960px)';

const CONTENT_PANEL_ROUTES = ['bots', 'projects', 'sources', 'results', 'apis', 'app-settings', 'lmr', 'validator'];


@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    A11yModule,
    NgClass,
    Dir,
    NgProgressbar,
    NgProgressHttp,
    HeaderComponent,
    MatSidenavContainer,
    MatSidenav,
    SidebarComponent,
    SidebarNoticeComponent,
    MatSidenavContent,
    TopmenuComponent,
    RouterOutlet,
    TwoFactorAuthMonitorComponent,
    SurveyIconComponent,
    AsyncPipe,
    MultiSummaryComponent,
  ],
})
export class AdminLayoutComponent implements OnDestroy, OnInit {
  @ViewChild('sidenav', { static: true }) sidenav!: MatSidenav;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;

  options = this.settings.getOptions();
  currentRouteSegment = '';

  private layoutChangesSubscription = Subscription.EMPTY;

  // Login popup properties
  isPopupMinimized$: Observable<boolean>;
  popupContent$: Observable<string>;
  isPopupEnabled$: Observable<boolean>;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  private isMobileScreen = false;

  @HostBinding('class.matero-content-width-fix') get contentWidthFix() {
    return (
      this.isContentWidthFixed &&
      this.options.navPos === 'side' &&
      this.options.sidenavOpened &&
      !this.isOver
    );
  }

  private isContentWidthFixed = true;

  @HostBinding('class.matero-sidenav-collapsed-fix') get collapsedWidthFix() {
    return (
      this.isCollapsedWidthFixed &&
      (this.options.navPos === 'top' || (this.options.sidenavOpened && this.isOver))
    );
  }

  @HostBinding('class.dashboard-route') get isDashboardRoute() {
    return this.currentRouteSegment === 'dashboard';
  }

  get shouldShowSidebarToggle(): boolean {
    // Dashboard has no left `ContentShell`; the global toggle only narrows the nav elsewhere
    // and `emitDrawerToggle` has no panel to collapse here, so hide it to avoid a no-op control.
    return !this.isOver && this.currentRouteSegment !== 'dashboard';
  }

  private isCollapsedWidthFixed = false;

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private settings: SettingsService,
    private loginPopupService: LoginPopupService,
    private exitSurveyService: ExitSurveyService,
    private layoutService: LayoutService,
    private apiAvailability: ApiAvailabilityService
  ) {
    // Initialize popup observables
    this.isPopupMinimized$ = this.loginPopupService.isMinimized$;
    this.popupContent$ = this.loginPopupService.popupContent$;
    this.isPopupEnabled$ = this.loginPopupService.isEnabled$;

    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_MEDIAQUERY, TABLET_MEDIAQUERY, MONITOR_MEDIAQUERY])
      .subscribe(state => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;

        this.isMobileScreen = state.breakpoints[MOBILE_MEDIAQUERY];
        this.options.sidenavCollapsed = state.breakpoints[TABLET_MEDIAQUERY];
        this.isContentWidthFixed = state.breakpoints[MONITOR_MEDIAQUERY];
      });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isOver) {
        this.sidenav.close();
      }
      this.content.scrollTo({ top: 0 });
      this.updateHeaderVisibility();
    });
  }

  ngOnInit() {
    this.toggleSidebar();
    this.updateHeaderVisibility();
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  protected readonly isApiUnavailable = this.apiAvailability.isUnavailable;

  protected retryConnection(): void {
    this.apiAvailability.clear();
    window.location.reload();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  // TODO: Trigger when transition end
  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  toggleSidebar() {
    this.layoutService.emitDrawerToggle();
  }

  private updateHeaderVisibility(): void {
    this.currentRouteSegment = this.router.url.split(';')[0].split('?')[0].split('/')[1];
    this.options.showHeader = !CONTENT_PANEL_ROUTES.includes(this.currentRouteSegment);
  }

  // Login popup methods
  onReopenPopup() {
    this.loginPopupService.reopenPopup();
  }

  onDismissPopup() {
    this.loginPopupService.dismissPermanently();
  }
}
